import { createApi } from "@reduxjs/toolkit/query/react"
import axiosBaseQuery from "../../../axiosBaseQuery"
import { requestData } from "../../../../api/csrfMutation"
import {
  LICENSE_ENDPOINTS,
  MODULE_ENDPOINTS,
  SIGNING_KEY_ENDPOINTS,
} from "../../../../api/constants"

/**
 * RTK Query API лицензирования EyeHub (EyeHub — ISSUER).
 *
 * Операторская (провайдерская) плоскость живёт под префиксом `admin/`:
 *   - `admin/licenses/` — список / деталь / выпуск лицензии + переходы состояния
 *     revoke / suspend / reinstate;
 *   - `admin/keys/` — ключи подписи Ed25519 (список / выпуск / ротация).
 * Пути без префикса (`licenses/activate|validate|deactivate`) — машинный контракт
 * клиники, порталом НЕ вызывается (см. api/constants.js).
 *
 * Контракт лицензии (деталь `GET admin/licenses/{id}/` — единственный источник
 * `modules[]`): {id, public_key, org, installation, license_type, status,
 * organization_name, branch_name, branch_city, machine_fingerprint, activation_id,
 * valid_until, max_days_without_sync, last_synced_at, blocked_reason, modules[],
 * created_at}; modules[] = {id, module_slug, active, blocked, blocked_reason,
 * request_limit, requests_used, requests_remaining, period_end, overage_price,
 * overage_available}.
 *
 * Контракт ключа подписи: {id, key_id, algorithm, public_key_b64, active,
 * created_at} — приватный ключ не отдаётся никогда.
 *
 * Списки — UnifiedOffsetPagination ({results, count, pageIndex, pageCount, ...}).
 */
export const licensesApi = createApi({
  reducerPath: "licensesApi",
  baseQuery: axiosBaseQuery(),
  tagTypes: ["License", "SigningKey", "Module"],
  endpoints: builder => ({
    previewHubInferenceControl: builder.mutation({
      query: ({ licenseId, ...params }) => ({
        url: `${LICENSE_ENDPOINTS.DETAIL(licenseId)}modules/hub-accounting/inference/`, method: "GET", params,
      }),
    }),
    changeHubInferenceControl: builder.mutation({
      async queryFn({ licenseId, csrfToken, ...data }) {
        try {
          return { data: await requestData({
            url: `${LICENSE_ENDPOINTS.DETAIL(licenseId)}modules/hub-accounting/inference/`, method: "POST", data,
            headers: { "X-CSRFToken": csrfToken },
          }) }
        } catch (error) {
          return { error: { status: error.response?.status, data: error.response?.data?.error || { message: "HUB inference control failed" } } }
        }
      },
      invalidatesTags: (result, error, { licenseId }) => [{ type: "License", id: licenseId }],
    }),
    previewHubAccounting: builder.mutation({
      query: ({ licenseId, ...params }) => ({
        url: `${LICENSE_ENDPOINTS.DETAIL(licenseId)}modules/hub-accounting/`, method: "GET", params,
      }),
    }),
    enableHubAccounting: builder.mutation({
      async queryFn({ licenseId, csrfToken, ...data }) {
        try {
          const result = await requestData({
            url: `${LICENSE_ENDPOINTS.DETAIL(licenseId)}modules/hub-accounting/`, method: "POST", data,
            headers: { "X-CSRFToken": csrfToken },
          })
          return { data: result }
        } catch (error) {
          return { error: { status: error.response?.status, data: error.response?.data?.error || { message: "HUB accounting activation failed" } } }
        }
      },
      invalidatesTags: (result, error, { licenseId }) => [{ type: "License", id: licenseId }],
    }),
    // === Лицензии ===
    getLicenses: builder.query({
      query: ({
        limit = 20,
        offset = 0,
        search,
        status,
        licenseType,
        org,
        ordering,
      } = {}) => ({
        url: LICENSE_ENDPOINTS.LIST,
        method: "GET",
        params: {
          limit,
          offset,
          ...(search ? { search } : {}),
          ...(status ? { status } : {}),
          ...(licenseType ? { license_type: licenseType } : {}),
          ...(org ? { org } : {}),
          ...(ordering ? { ordering } : {}),
        },
      }),
      providesTags: [{ type: "License", id: "LIST" }],
    }),

    // Деталь: entitlements (modules[]) и диагностика привязки к железу.
    getLicense: builder.query({
      query: id => ({ url: LICENSE_ENDPOINTS.DETAIL(id), method: "GET" }),
      providesTags: (result, error, id) => [{ type: "License", id }],
    }),

    // Выпуск лицензии. Поле организации в контракте называется `org`
    // (не `organization`): {org, installation?, license_type, organization_name?}.
    createLicense: builder.mutation({
      query: data => ({ url: LICENSE_ENDPOINTS.CREATE, method: "POST", data }),
      invalidatesTags: [{ type: "License", id: "LIST" }],
    }),

    revokeLicense: builder.mutation({
      query: id => ({ url: LICENSE_ENDPOINTS.REVOKE(id), method: "POST" }),
      invalidatesTags: (result, error, id) => [
        { type: "License", id },
        { type: "License", id: "LIST" },
      ],
    }),

    suspendLicense: builder.mutation({
      query: id => ({ url: LICENSE_ENDPOINTS.SUSPEND(id), method: "POST" }),
      invalidatesTags: (result, error, id) => [
        { type: "License", id },
        { type: "License", id: "LIST" },
      ],
    }),

    reinstateLicense: builder.mutation({
      query: id => ({ url: LICENSE_ENDPOINTS.REINSTATE(id), method: "POST" }),
      invalidatesTags: (result, error, id) => [
        { type: "License", id },
        { type: "License", id: "LIST" },
      ],
    }),

    // === Ключи подписи (hub-admin) ===
    // Без активного ключа activate/validate отдают 503 SIGNING_KEY_UNAVAILABLE —
    // флот перестаёт валидировать лицензии, поэтому список нужен и на /licenses.
    getSigningKeys: builder.query({
      query: ({ limit = 50, offset = 0, active } = {}) => ({
        url: SIGNING_KEY_ENDPOINTS.LIST,
        method: "GET",
        params: {
          limit,
          offset,
          ...(active === undefined || active === null ? {} : { active }),
        },
      }),
      providesTags: [{ type: "SigningKey", id: "LIST" }],
    }),

    createSigningKey: builder.mutation({
      query: data => ({
        url: SIGNING_KEY_ENDPOINTS.CREATE,
        method: "POST",
        data,
      }),
      invalidatesTags: [{ type: "SigningKey", id: "LIST" }],
    }),

    // Ротация: старый ключ деактивируется, создаётся новый активный.
    // `keyId` нового ключа опционален (сервер сгенерирует сам).
    rotateSigningKey: builder.mutation({
      query: ({ id, keyId } = {}) => ({
        url: SIGNING_KEY_ENDPOINTS.ROTATE(id),
        method: "POST",
        data: keyId ? { key_id: keyId } : {},
      }),
      invalidatesTags: [{ type: "SigningKey", id: "LIST" }],
    }),

    // === Каталог AI-модулей и подписки лицензии (hub-admin) ===
    // Хуки этих endpoint'ов уже импортировались страницами Licenses/LicenseDetail,
    // но сами endpoint'ы не были определены: RTK Query тогда не генерирует хук, и
    // `useGetModulesQuery(...)` падает как `is not a function` — страница «Лицензии»
    // роняла ErrorBoundary целиком. Контракт — `licensing/views.py`
    // (ModuleViewSet + @action modules/reset-usage/module_detail).

    // Каталог модулей — источник допустимых слагов для подписки (свободный ввод
    // недопустим: клиника ищет точное совпадение слага). `available=true` прячет
    // снятые с продажи модули.
    getModules: builder.query({
      query: ({ available, limit = 100, offset = 0 } = {}) => ({
        url: MODULE_ENDPOINTS.LIST,
        method: "GET",
        params: {
          limit,
          offset,
          ...(available === undefined || available === null ? {} : { available }),
        },
      }),
      providesTags: [{ type: "Module", id: "LIST" }],
    }),

    // Создать/обновить подписку лицензии на модуль. Именно это наполняет
    // `entitlements.modules` в подписанном ответе validate; инвалидируем деталь
    // лицензии, чтобы обновлённые лимиты/статусы сразу отрисовались.
    setLicenseModule: builder.mutation({
      query: ({ licenseId, ...data }) => ({
        url: LICENSE_ENDPOINTS.MODULES(licenseId),
        method: "POST",
        data,
      }),
      invalidatesTags: (result, error, { licenseId }) => [
        { type: "License", id: licenseId },
      ],
    }),

    // Снять подписку целиком (для временного отключения — setLicenseModule с
    // active=false, оно сохраняет накопленное потребление).
    removeLicenseModule: builder.mutation({
      query: ({ licenseId, moduleSlug }) => ({
        url: LICENSE_ENDPOINTS.MODULE_DETAIL(licenseId, moduleSlug),
        method: "DELETE",
      }),
      invalidatesTags: (result, error, { licenseId }) => [
        { type: "License", id: licenseId },
      ],
    }),

    // Сбросить счётчик потребления модуля (новый период). Без сброса модуль с
    // overage_available=false остаётся заблокированным навсегда после первого
    // превышения лимита.
    resetLicenseModuleUsage: builder.mutation({
      query: ({ licenseId, moduleSlug }) => ({
        url: LICENSE_ENDPOINTS.MODULE_RESET_USAGE(licenseId),
        method: "POST",
        data: { module_slug: moduleSlug },
      }),
      invalidatesTags: (result, error, { licenseId }) => [
        { type: "License", id: licenseId },
      ],
    }),
  }),
})

export const {
  usePreviewHubAccountingMutation,
  usePreviewHubInferenceControlMutation,
  useChangeHubInferenceControlMutation,
  useEnableHubAccountingMutation,
  useGetLicensesQuery,
  useGetLicenseQuery,
  useCreateLicenseMutation,
  useGetModulesQuery,
  useSetLicenseModuleMutation,
  useRemoveLicenseModuleMutation,
  useResetLicenseModuleUsageMutation,
  useRevokeLicenseMutation,
  useSuspendLicenseMutation,
  useReinstateLicenseMutation,
  useGetSigningKeysQuery,
  useCreateSigningKeyMutation,
  useRotateSigningKeyMutation,
} = licensesApi

export default licensesApi
